import { Component, Input, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ScreenHeatmapComponent } from './screen-heatmap.component';
import { ReplayViewerComponent } from '../../../public/credee-demo/viewer/viewer.component';
import { ScreenView, screenViews } from '../insights-screens.util';
import { parseApiMs } from '../tracking-format.util';
import type { ReportEvent, ReportRecording, SessionInsights } from '../tracking-demo.models';
import { WebsiteRejectionsComponent } from './website-rejections.component';

/** The two views inside the Insights tab; the first is the default. */
type InsightsView = 'heatmap' | 'replay';

/**
 * One demo's Session Insights: behavioural tiles (clicks / rage-clicks / dead-clicks /
 * scroll depth) always on top, then a segmented toggle between the interactive rrweb
 * replay and the click heatmaps — one per screen the visitor saw (lobby, live call),
 * each drawn over a paused frame of that screen with its most-clicked elements.
 *
 * Its own component so session-report.component.ts (already over the file budget) only
 * wires it. The replay viewer is @defer-loaded so its ~1.2 MB rrweb dependency stays out
 * of the dashboard's main chunk until the Insights tab renders (replay is the default view).
 */
@Component({
  selector: 'app-session-insights-panel',
  standalone: true,
  imports: [CommonModule, ScreenHeatmapComponent, ReplayViewerComponent, WebsiteRejectionsComponent],
  // The report renders this inside a bounded flex column (fixed pane). The host must be
  // a sized flex item too, or the inner region has no height to fit/scroll within.
  host: { class: 'flex min-h-0 flex-1 flex-col' },
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <!-- Setup-form website refusals at Join (setup.website_rejected); renders nothing when none. -->
    <app-website-rejections [events]="events" />
    @if (insights() || domSrc()) {
      <div class="flex min-h-0 flex-1 flex-col gap-4">
        <!-- behavioural tiles -->
        <div class="grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-4">
          @for (t of tiles(); track t.label) {
            <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-3">
              <div class="text-[11px] font-medium text-gray-500 dark:text-gray-400">{{ t.label }}</div>
              <div class="mt-1 text-xl font-semibold" [class]="t.accent">{{ t.value }}</div>
            </div>
          }
        </div>

        <!-- view toggle -->
        <div class="flex shrink-0 gap-1 self-start rounded-lg border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-1">
          @for (v of views; track v.key) {
            <button type="button" (click)="view.set(v.key)"
              class="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
              [class]="view() === v.key
                ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300 shadow-sm'
                : 'text-slate-500 hover:text-slate-800 dark:text-ink-soft/60 dark:hover:text-white'">
              {{ v.label }}
            </button>
          }
        </div>

        <!-- Both views are bounded flex columns so their content contain-fits the pane
             (no scrolling) — the replay viewer and each screen card size themselves. -->
        <div class="flex min-h-0 flex-1 flex-col">
          @if (view() === 'heatmap') {
            @if (screens().length) {
              <div class="grid min-h-0 flex-1 auto-rows-[minmax(0,1fr)] gap-4 xl:grid-cols-2">
                @for (s of screens(); track s.name) {
                  <app-screen-heatmap [title]="s.label" [src]="domSrc()" [atMs]="s.atMs"
                    [points]="s.cells" [aspect]="aspect()" [clicks]="s.clicks" [markers]="s.markers" />
                }
              </div>
              <div class="mt-2 shrink-0 text-[10px] text-slate-400">
                Each screen's clicks over a frame of that screen, normalized to the recorded viewport — hotter = more clicks.
              </div>
            } @else {
              <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-6 text-center text-sm text-slate-400">
                No clicks were captured for this demo.
              </div>
            }
          } @else {
            <div class="mb-1.5 shrink-0 text-[11px] font-medium uppercase tracking-wide text-slate-400">Session replay</div>
            <div class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-[#EAEAEA] dark:border-line bg-[#0f172a]">
              @if (domSrc(); as src) {
                @defer (on immediate) {
                  <app-replay-viewer [embedded]="true" [audioStartMs]="audioStartMs()" [src]="src" />
                } @loading {
                  <div class="flex h-40 items-center justify-center text-xs text-slate-400">Loading replay…</div>
                }
              } @else {
                <div class="flex h-40 items-center justify-center text-xs text-slate-400">
                  No interactive replay for this session.
                </div>
              }
            </div>
            <div class="mt-1 shrink-0 text-[10px] text-slate-400">
              DOM replay: page, slides, clicks and cursor. Webcam/screen video is on the Video tab.
            </div>
          }
        </div>
      </div>
    } @else {
      <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-6 text-center text-sm text-slate-400">
        No session insights captured for this demo.
      </div>
    }
  `,
})
export class SessionInsightsPanelComponent {
  @Input() recording: ReportRecording | null = null;
  /** The report's event log — only the website-rejection rows are read from it. */
  @Input() events: ReportEvent[] | null = null;

  readonly views: Array<{ key: InsightsView; label: string }> = [
    { key: 'replay', label: 'Session replay' },
    { key: 'heatmap', label: 'Click heatmap' },
  ];
  /** Session replay opens first — the primary "what did they see" view. */
  readonly view = signal<InsightsView>('replay');

  insights(): SessionInsights | null {
    return this.recording?.insights ?? null;
  }

  domSrc(): string | null {
    return this.recording?.domUrl ?? null;
  }

  /** When the call audio starts (Join) — the replay shifts its audio by this. */
  audioStartMs(): number | null {
    return parseApiMs(this.recording?.audioStartTime ?? this.recording?.startTime);
  }

  /** This session's clicks split per screen (lobby / live call), with top elements. */
  screens(): ScreenView[] {
    return screenViews(this.insights());
  }

  /** Heatmap box aspect = the recorded viewport's, so clicks land where they were. */
  aspect(): number {
    const vp = this.insights()?.viewport;
    return vp && vp.w > 0 && vp.h > 0 ? vp.w / vp.h : 16 / 9;
  }

  tiles(): { label: string; value: string | number; accent: string }[] {
    const i = this.insights();
    const rage = i?.rageClicks ?? 0;
    const dead = i?.deadClicks ?? 0;
    return [
      { label: 'Clicks', value: i?.clickCount ?? 0, accent: 'text-slate-800 dark:text-gray-100' },
      { label: 'Rage clicks', value: rage,
        accent: rage > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-gray-100' },
      { label: 'Dead clicks', value: dead,
        accent: dead > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-800 dark:text-gray-100' },
      { label: 'Max scroll', value: `${i?.maxScrollPx ?? 0}px`, accent: 'text-slate-800 dark:text-gray-100' },
    ];
  }
}
